function resourcesOf(source) {
  const resources=new Set(),roots=new Set(source.scenes??[source.scene??source]);
  const texture=value=>{
    if(value?.isTexture)resources.add(value);
    else if(Array.isArray(value))for(const item of value)texture(item);
  };
  for(const root of roots)root.traverse(node=>{
    if(node.geometry)resources.add(node.geometry);
    for(const material of Array.isArray(node.material)?node.material:node.material?[node.material]:[]){
      resources.add(material);
      for(const value of Object.values(material))texture(value);
      if(material.uniforms)for(const uniform of Object.values(material.uniforms))texture(uniform.value);
    }
    texture(node.skeleton?.boneTexture);
  });
  return resources;
}

// Use only after the last external owner releases its clones. Material.clone()
// shares textures, so disposing a source while it is displayed is unsafe.
export function disposeModel(source) {
  for(const resource of resourcesOf(source))resource.dispose();
}

export class ModelCache {
  constructor(load,{limit=4}={}) {
    if(!Number.isInteger(limit)||limit<1)throw new RangeError('Model cache limit must be a positive integer');
    this.load=load;this.limit=limit;this.entries=new Map();this.retained=new Set();this.references=new Map();this.clock=0;
  }

  get completedCount() {
    let count=0;for(const entry of this.entries.values())if(entry.resources)count++;
    return count;
  }

  // Retain current and incoming keys BEFORE awaiting get(). Unretained requests
  // are prefetches and may be evicted when another load completes.
  get(key) {
    const existing=this.entries.get(key);
    if(existing){existing.used=++this.clock;return existing.promise;}
    const entry={key,used:++this.clock,resources:null,promise:null};
    this.entries.set(key,entry);
    entry.promise=Promise.resolve().then(()=>this.load(key)).then(source=>{
      entry.resources=resourcesOf(source);
      for(const resource of entry.resources)this.references.set(resource,(this.references.get(resource)??0)+1);
      if(this.entries.get(key)!==entry){
        this.releaseResources(entry);
        const error=new Error('Model request was cleared before completion');error.name='AbortError';throw error;
      }
      this.trim();return source;
    }).catch(error=>{
      // An older rejected request must not erase its replacement after clear().
      if(this.entries.get(key)===entry)this.entries.delete(key);
      throw error;
    });
    return entry.promise;
  }

  // Change pins only after disposing old display clones. More than limit pins
  // temporarily exceeds the budget; their textures remain safe until unpinned.
  retain(keys) {
    this.retained=new Set();for(const key of keys)if(key!==null&&key!==undefined)this.retained.add(key);
    this.trim();
  }

  trim() {
    let completed=this.completedCount;
    while(completed>this.limit){
      let oldest=null;
      for(const entry of this.entries.values()){
        if(entry.resources&&!this.retained.has(entry.key)&&(!oldest||entry.used<oldest.used))oldest=entry;
      }
      if(!oldest)break;
      this.entries.delete(oldest.key);this.releaseResources(oldest);completed--;
    }
  }

  releaseResources(entry) {
    for(const resource of entry.resources??[]){
      const remaining=this.references.get(resource)-1;
      if(remaining>0)this.references.set(resource,remaining);
      else {this.references.delete(resource);resource.dispose();}
    }
    entry.resources=null;
  }

  // Dispose displayed clones first. Pending loads cannot be aborted by a
  // generic loader; their eventual results are disposed and reject as aborted.
  clear() {
    for(const entry of this.entries.values())this.releaseResources(entry);
    this.entries.clear();this.retained.clear();
  }
}
